import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { TallyRow } from "./reveal-rows";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { formatPrizeTitle } from "@/utils/helpers";

/**
 * One line of the tally: how many of this prize the player just revealed, what it is, and the
 * games it plays on.
 *
 * The prize title is priority content (`external-widget/CLAUDE.md`) — it wraps and is never
 * truncated. The count chip sits before it so the title always starts at the same left edge, and
 * takes the space it needs.
 */
export const PrizeTallyRow = ({
  row,
  variants,
}: {
  row: TallyRow;
  variants: Parameters<typeof motion.div>[0]["variants"];
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      variants={variants}
      className="flex items-start gap-3 rounded-(--border-radius-inner) bg-background-card px-4 py-3.5"
    >
      {/* Only above 1: a "×1" chip is noise on a single prize. */}
      {row.count > 1 && (
        <span className="shrink-0 rounded-md border border-prizes/40 bg-prizes/15 px-2 py-0.5 font-heading text-[12px] leading-snug font-bold text-prizes">
          {t("prizeReveal.countChip", "×{{count}}", { count: row.count })}
        </span>
      )}
      <span className="flex grow flex-col gap-1.5">
        <span className="font-heading text-[17px] leading-snug font-semibold text-foreground">
          {formatPrizeTitle(row.rawTitle, t)}
        </span>
        {/* The shared games line with the names spelled out, no dialog: this window has one call
            to action. Renders nothing for a cash/bonus prize, so no guard is needed here. */}
        <PrizeGamesChips
          games={row.games}
          browsableRemainder={false}
          className="flex flex-wrap items-center gap-1.5"
        />
      </span>
    </motion.div>
  );
};
